// src/components/countries/CountriesTable.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, useTheme } from "@mui/material";

import useGetCountries from "../../hooks/countries/useGetCountries";
import useDeleteCountries from "../../hooks/countries/useDeleteCountries";
import useDeleteAllCountries from "../../hooks/countries/useDeleteAllCountries";
import useRestoreCountry from "../../hooks/countries/useRestoreCountry";
import useRestoreAllCountries from "../../hooks/countries/useRestoreAllCountries";
import useForceDeleteCountry from "../../hooks/countries/useForceDeleteCountry";
import useForceDeleteAllCountries from "../../hooks/countries/useForceDeleteAllCountries";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import CountriesTableRow from "./Table/CountriesTableRow";
import CountriesTableColumns from "./Table/CountriesTableColumns";
import ArchiveButton from "../global/Table/ArchiveButton";
import toast from "react-hot-toast";
import { usePermissions } from "../../auth/PermissionsContext";

const CountriesTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("countries_list"),
            create: has("countries_create"),
            edit: has("countries_edit"),
            delete: has("countries_delete"),
            forceDelete: has("countries_force_delete"),
            archiveList: has("countries_archive_list"),
            restore: has("countries_restore"),
            restoreAll: has("countries_restore-all-table"),
            deleteAll: has("countries_delete-all-table"),
            massForceDeleteAll: has("countries_mass_force_delete-all-table"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const isArchive = location.pathname.includes("/countries/archive");

    // CRUD Hooks
    const { deleteCountries, isDelete } = useDeleteCountries();
    const { deleteAllCountries, isDeleteAll } = useDeleteAllCountries();
    const { restoreCountry, isRestore } = useRestoreCountry();
    const { restoreAllCountries, isRestoreAll } = useRestoreAllCountries();
    const { forceDeleteCountry, isForceDelete } = useForceDeleteCountry();
    const { forceDeleteAllCountries, isForceDeleteAll } = useForceDeleteAllCountries();

    // ✅ استخدام Custom Hook للإجراءات المشتركة
    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
        setSelectedIds,
    } = useTableActions({
        basePath: "/countries",
        deleteSingle: deleteCountries,
        deleteAll: deleteAllCountries,
        restoreSingle: restoreCountry,
        restoreAll: restoreAllCountries,
        forceDeleteSingle: forceDeleteCountry,
        forceDeleteAll: forceDeleteAllCountries,
        isArchive,
    });

    const filters = useMemo(
        () => ({
            search: search || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            ...(isArchive ? { only_trashed: true } : {}),
        }),
        [search, sortBy, sortDir, isArchive]
    );

    const { isLoading, error, countries, meta } = useGetCountries(page, perPage, filters);

    const items = useMemo(() => countries?.items || [], [countries]);

    const rowMenuDisabled = isArchive
        ? !perms.restore && !perms.forceDelete
        : !perms.edit && !perms.delete;

    const renderRow = (params) => (
        <CountriesTableRow
            key={params.item.id}
            country={params.item}
            index={params.index}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            totalItems={items.length}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            isArchive={isArchive}
            menuDisabled={rowMenuDisabled}
        />
    );

    const bulkDeleteDisabled = isArchive ? !perms.massForceDeleteAll : !perms.deleteAll;

    const title = isArabic ? "الدول" : "Countries";
    const subtitle = isArchive
        ? isArabic
            ? "عرض الدول المحذوفة (الأرشيف)"
            : "Archived countries (soft deleted)"
        : isArabic
            ? "إدارة الدول المتاحة في النظام"
            : "Manage available countries in the system";

    const addButtonDisabled = isArchive
        ? !perms.restoreAll || selectedIds.length === 0
        : !perms.create;

    const addButtonLabel = isArchive
        ? isRestoreAll
            ? isArabic
                ? "جاري الاستعادة..."
                : "Restoring..."
            : isArabic
                ? "استعادة المحدد"
                : "Restore selected"
        : isArabic
            ? "إضافة دولة"
            : "Create new country";

    const addButtonAction = isArchive
        ? () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic ? "ليس لديك صلاحية للاستعادة" : "No restore permission"
                );
                return;
            }

            if (isRestoreAll) return;

            if (!selectedIds.length) {
                toast.error(
                    isArabic
                        ? "الرجاء تحديد العناصر المراد استرجاعها أولاً"
                        : "Please select items to restore first"
                );
                return;
            }

            restoreAllCountries({ items: selectedIds });
            setSelectedIds([]);
        }
        : () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic ? "ليس لديك صلاحية للإنشاء" : "No create permission"
                );
                return;
            }

            navigate("/countries/create");
        };

    return (
        <>
            {!isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/countries/archive"
                        visible={perms.archiveList}
                        disabled={!perms.archiveList}
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                </Stack>
            )}
            {isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/countries"
                        visible={true}
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to list"
                    />
                </Stack>
            )}
            <TableLayout
                title={title}
                subtitle={subtitle}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={addButtonLabel}
                onAddClick={addButtonAction}
                addButtonVisible={true}
                isLoading={isLoading}
                error={error}
                items={items}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={CountriesTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuDelete={handleMenuDelete}
                onMenuRestore={isArchive ? handleMenuRestore : undefined}
                onMenuEdit={!isArchive ? handleEdit : undefined}
                menuDisableEdit={!isArchive && !perms.edit}
                menuDisableRestore={isArchive && !perms.restore}
                menuDisableDelete={isArchive ? !perms.forceDelete : !perms.delete}
                isDeletingSingle={isArchive ? isRestore || isForceDelete : isDelete}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, items)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                bulkDeleteDisabled={bulkDeleteDisabled}
                isBulkDeleting={isArchive ? isForceDeleteAll : isDeleteAll}
                addButtonDisabled={addButtonDisabled}
                addButtonTooltip={
                    addButtonDisabled
                        ? isArabic
                            ? "لا تملك صلاحية تنفيذ هذا الإجراء"
                            : "You don't have permission"
                        : undefined
                }
                emptyLabelAr={isArchive ? "لا توجد دول في الأرشيف" : "لا توجد دول"}
                emptyLabelEn={isArchive ? "No archived countries" : "No countries"}
                loadErrorAr="حدث خطأ أثناء تحميل الدول"
                loadErrorEn="Failed to load countries"
            />
        </>
    );
};

export default CountriesTable;
